/**
 * useFirstChallengeTarget — attaches an inert measurement ref to a real element. When the tour reaches
 * the step that spotlights this target, it measures the element's on-screen rect and writes it to the
 * store. Purely additive: the host component keeps its behaviour/appearance; the ref only measures while
 * the tour is on this step. Mirrors useWalkthroughTarget but for the first-challenge store.
 * See specs/first-challenge-onboarding/spec.md.
 */

import { useEffect, useRef } from 'react';
import { View } from 'react-native';
import { useFirstChallengeTourStore } from '../stores/firstChallengeTourStore';
import { FC_STEP_CONFIG } from '../constants/firstChallengeTour.constants';
import { SPOTLIGHT_PADDING } from '../../create/constants/guestWalkthrough.constants';

/** True when the tour's current step spotlights this target key. */
function isCurrentTarget(targetKey: string): boolean {
  const { active, step } = useFirstChallengeTourStore.getState();
  return active && step != null && FC_STEP_CONFIG[step].targetKey === targetKey;
}

/**
 * @param targetKey which spotlight target this element is.
 * @param externalRef optionally measure an EXISTING ref (e.g. one already attached for the legacy
 *   walkthrough) instead of attaching a new one — lets a component be spotlighted with no JSX change.
 */
export function useFirstChallengeTarget(
  targetKey: string,
  externalRef?: React.RefObject<View | null>,
): { viewRef: React.RefObject<View | null> } {
  const internalRef = useRef<View>(null);
  const viewRef = externalRef ?? internalRef;
  const active = useFirstChallengeTourStore((s) => s.active);
  const step = useFirstChallengeTourStore((s) => s.step);

  const current = active && step != null && FC_STEP_CONFIG[step].targetKey === targetKey;

  useEffect(() => {
    if (!current) return;
    let cancelled = false;

    const measure = () => {
      if (cancelled || !isCurrentTarget(targetKey)) return;
      // Guard: some hosts (e.g. ScrollView) may not expose measureInWindow — skip gracefully.
      if (typeof viewRef.current?.measureInWindow !== 'function') return;
      viewRef.current.measureInWindow((x, y, width, height) => {
        if (cancelled || !isCurrentTarget(targetKey)) return;
        if (width === 0 && height === 0) return; // not laid out yet
        useFirstChallengeTourStore.getState().setTargetRect({
          x: x - SPOTLIGHT_PADDING,
          y: y - SPOTLIGHT_PADDING,
          width: width + SPOTLIGHT_PADDING * 2,
          height: height + SPOTLIGHT_PADDING * 2,
        });
      });
    };

    // Measure after paint, then again shortly after (the incoming screen may still be laying out).
    const raf = requestAnimationFrame(measure);
    const t = setTimeout(measure, 380);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [current, targetKey, viewRef]);

  return { viewRef };
}
